/**
 * @fileoverview
 * Pricing Section for the homepage with vertical sidebar layout matching live aideco.us.
 *
 * This component renders a sidebar + content pricing interface. The paid tiers
 * are always shown:
 * - Freelancer: $29/month, 25 renders/day
 * - Architect: $79/month, 100 renders/day
 * - Firm: $299/month, 500 renders/day
 *
 * A $0 Free tier (3 renders/day) is prepended when the paid monetization gate
 * is OFF (default, free-to-start). When the gate is ON, the Free tier is hidden
 * and new accounts begin with a Stripe-managed 3-day free trial (card required).
 *
 * Layout: Vertical sidebar on the left (~30%), content panel on the right (~70%)
 *
 * CTA buttons use data-open-onboarding attributes to trigger the onboarding
 * modal with the corresponding plan pre-selected.
 *
 * @see {@link useOnboardingState} for onboarding state management
 */

"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { DAILY_RENDER_LIMITS } from "@/components/marketing/data/render-credits";
import { Button } from "@/components/ui/button";
import { featureFlags } from "@/config/features";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { SectionContainer, SectionEyebrow, SectionShell } from "../primitives";

type PricingTier = {
  id: string;
  name: string;
  planValue: "free" | "starter" | "pro" | "studio";
  price: string;
  description: string;
  thumbnail: string;
  features: string[];
  ctaText: string;
};

/**
 * PricingSection renders the pricing section with vertical sidebar layout matching live site.
 * Left sidebar with stacked plan buttons, right content panel with thumbnail and details.
 *
 * Defaults to the Free tier (gate OFF) or Freelancer/Starter tier (gate ON) on first paint.
 *
 * Uses native React state management.
 *
 * @returns JSX.Element
 */
export function PricingSection() {
  const { t } = useLingui();
  // Paid gate (NEXT_PUBLIC_MONETIZATION_GATE) controls whether a Free tier is
  // offered. Default (free-to-start) prepends a $0 Free tier and selects it on
  // first paint; paid gate hides it and defaults to the Freelancer (Starter) tier.
  const showFreeTier = !featureFlags.enablePaidGate;
  const [activePlan, setActivePlan] = useState<string>(showFreeTier ? "free" : "freelancer");
  const { actions } = useOnboardingState();
  const pricingTiers: PricingTier[] = useMemo(() => [
    ...(showFreeTier
      ? [
          {
            id: "free",
            name: t`Free`,
            planValue: "free" as const,
            price: t`Free`,
            description:
              t`Perfect for freelancers and small projects. Get started with AI-powered renders at no cost and upgrade when you need more.`,
            thumbnail: "/images/marketing/68651cbe4ef0a88eec912dc5_1U.avif",
            features: [
              t`Price: Free`,
              t`Credits: ${DAILY_RENDER_LIMITS.free.full}`,
              t`Resolution: Up to 720p`,
              t`Styles: 5 preset styles`,
              t`Support: Community forum`,
            ],
            ctaText: t`Get Started Free`,
          },
        ]
      : []),
    {
      id: "freelancer",
      name: t`Freelancer`,
      planValue: "starter",
      price: "$29/month",
      description:
        t`For professional architects who need consistent, high-quality renders for client presentations and project proposals.`,
      thumbnail: "/images/marketing/6865206a7dbac8b4c368e876_24.avif",
      features: [
        t`Price: $29/month`,
        t`Credits: ${DAILY_RENDER_LIMITS.starter.full}`,
        t`Resolution: Up to 1080p`,
        t`Styles: All styles + custom prompts`,
        t`Support: Email support`,
      ],
      ctaText: t`Choose Freelancer`,
    },
    {
      id: "architect",
      name: t`Architect`,
      planValue: "pro",
      price: "$79/month",
      description:
        t`Built for architecture firms and design studios. High volume rendering with priority processing queue.`,
      thumbnail: "/images/marketing/686520d072d2920c1bb09330_34.avif",
      features: [
        t`Price: $79/month`,
        t`Credits: ${DAILY_RENDER_LIMITS.pro.full}`,
        t`Resolution: Up to 4K`,
        t`Styles: All styles + advanced controls`,
        t`Support: Priority email support`,
        t`Risk-Free: Money-back guarantee`,
      ],
      ctaText: t`Choose Architect`,
    },
    {
      id: "firm",
      name: t`Firm`,
      planValue: "studio",
      price: "$299/month",
      description:
        t`For large firms and studios. Maximum capacity with dedicated support and custom integrations.`,
      thumbnail: "/images/marketing/68652144148bb561413dbc1b_44.avif",
      features: [
        t`Price: $299/month`,
        t`Credits: ${DAILY_RENDER_LIMITS.studio.full}`,
        t`Resolution: Up to 4K+`,
        t`Features: All premium features`,
        t`Support: Dedicated account manager`,
        t`Risk-Free: Money-back guarantee`,
      ],
      ctaText: t`Choose Firm`,
    },
  ], [t, showFreeTier]);

  const activeTier = pricingTiers.find((tier) => tier.id === activePlan) || pricingTiers[0];

  const handlePlanCta = () => {
    actions.setPlan(activeTier.planValue);
    actions.openModal();
  };

  return (
    <SectionShell
      id="pricing"
      ariaLabel={t`Pricing section`}
      noPadding
      className="py-6 sm:py-12 lg:py-16"
    >
      <SectionContainer>
        <div className="flex max-w-[600px] flex-col gap-5 md:gap-10 lg:gap-16">
          <SectionEyebrow label={t`Pricing`} align="left" backgroundClassName="bg-[#f5f5f5]" />
          <h2 className="max-w-full font-serif-display text-2xl font-bold italic leading-tight text-gray-900 sm:text-3xl lg:text-4xl">
            <Trans>Simple credit-based pricing for every team size</Trans>
          </h2>
        </div>

        {/* Vertical sidebar (left) + content panel (right) */}
        <div className="mt-10 rounded-[10px] bg-white p-2.5 md:mt-12 lg:mt-16">
          <div className="flex flex-col gap-2.5 lg:flex-row">
            {/* Sidebar: stacked plan buttons */}
            <div className="flex w-full flex-col gap-2.5 lg:w-[25%] lg:max-w-[260px]" role="tablist">
              {pricingTiers.map((tier) => (
                <button
                  key={tier.id}
                  className={`flex w-full cursor-pointer items-center justify-between rounded-lg px-5 py-4 text-base font-medium transition-all duration-200 ${
                    activePlan === tier.id
                      ? "bg-black text-white"
                      : "bg-[#f5f5f5] text-black hover:bg-gray-200"
                  }`}
                  data-testid={`pricing-tab-${tier.id}`}
                  onClick={() => {
                    setActivePlan(tier.id);
                  }}
                  role="tab"
                  type="button"
                  aria-selected={activePlan === tier.id}
                  tabIndex={activePlan === tier.id ? 0 : -1}
                >
                  <span>{tier.name}</span>
                  <span aria-hidden="true">→</span>
                </button>
              ))}
            </div>

            {/* Content panel */}
            <div className="w-full lg:w-[70%]">
              {pricingTiers.map((tier) => (
                <div
                  key={tier.id}
                  className="w-full"
                  data-testid={`pricing-panel-${tier.id}`}
                  role="tabpanel"
                  aria-hidden={activePlan !== tier.id}
                >
                  {activePlan === tier.id ? (
                    <div className="flex flex-col gap-5 rounded-lg bg-[#f5f5f5] p-5 sm:flex-row sm:gap-10 sm:p-8">
                      <div className="w-full sm:w-1/2 sm:max-w-[500px]">
                        <Image
                          alt={t`${tier.name} plan render example`}
                          className="h-auto max-h-[200px] w-full rounded-lg object-cover sm:max-h-[280px]"
                          height={400}
                          src={tier.thumbnail}
                          width={600}
                        />
                      </div>
                        <div className="flex w-full flex-col items-start justify-center gap-10 sm:w-1/2">
                          <div className="flex flex-col gap-10 md:gap-8">
                            <p className="text-sm text-gray-500 sm:text-base">{tier.description}</p>
                          <ul className="flex list-disc flex-col gap-6 pl-4" role="list">
                            {tier.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="text-sm text-gray-600">
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="mt-2 sm:mt-4">
                          <Button
                            className="bg-black text-sm font-semibold capitalize text-white hover:bg-gray-900"
                            data-open-onboarding={tier.planValue}
                            data-testid="pricing-cta"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePlanCta();
                            }}
                            type="button"
                          >
                            {tier.ctaText}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionContainer>
    </SectionShell>
  );
}
